import type { CalculatedZone, ZoneModel } from '../types/zones'

export interface ThresholdValues {
  vt1Bpm?: number | null
  vt2Bpm?: number | null
  maxHrBpm?: number | null
  vt1PaceSeconds?: number | null // Pace in seconds per km
  vt2PaceSeconds?: number | null // Pace in seconds per km
  vt1PowerWatts?: number | null
  vt2PowerWatts?: number | null
}

/**
 * Calculate HR zones based on VT1, VT2, and max HR
 * Zones are calculated relative to VT1 and VT2 with specific logic per zone
 */
export const calculateHRZones = (zoneModel: ZoneModel, thresholds: ThresholdValues): CalculatedZone[] => {
  const { vt1Bpm, vt2Bpm, maxHrBpm } = thresholds
  if (!vt1Bpm && !vt2Bpm) {
    return zoneModel.zones.map((zone) => ({
      ...zone,
      minValue: null,
      maxValue: null,
    }))
  }
  return zoneModel.zones.map((zone, index) => {
    const zones = computeFiveZoneHr(vt1Bpm, vt2Bpm, maxHrBpm)
    const chosen = zones[index] ?? { min: null, max: null }
    return {
      ...zone,
      minValue: chosen.min,
      maxValue: chosen.max,
    }
  })
}

const computeFiveZoneHr = (
  vt1Bpm?: number | null,
  vt2Bpm?: number | null,
  maxHrBpm?: number | null
): { min: number | null; max: number | null }[] => {
  // If we don’t have both breakpoints, return nulls
  if (!vt1Bpm || !vt2Bpm || vt1Bpm >= vt2Bpm) {
    return Array.from({ length: 5 }, () => ({ min: null, max: null }))
  }

  // Base targets before contiguity fixes
  const z1Min = 0
  const z1Max = Math.max(0, Math.round(vt1Bpm - 5))

  let z2Min = z1Max // will fix contiguity below
  let z2Max = Math.round(vt1Bpm)

  let z3Min = Math.round(vt1Bpm + 1)
  let z3Max = Math.round(vt2Bpm - 5)

  let z4Min = Math.round(vt2Bpm - 5)
  let z4Max = Math.round(vt2Bpm)

  const fallbackMax = vt2Bpm ? Math.round(vt2Bpm * 1.1) : null
  const z5Ceil = maxHrBpm ?? fallbackMax ?? null
  let z5Min = Math.round(vt2Bpm + 1)
  let z5Max = z5Ceil

  // Ensure contiguity and non-decreasing bounds (no gaps/overlaps)
  // Also ensure each zone has at least 1 bpm width where possible.
  const clamp = (min: number, max: number) => ({
    min: Math.max(0, Math.min(min, max)),
    max: Math.max(0, Math.max(min, max)),
  })

  // Z2 starts right after Z1
  z2Min = z1Max + 1
  ;({ min: z2Min, max: z2Max } = clamp(z2Min, z2Max))

  // Z3 starts right after Z2
  z3Min = Math.max(z3Min, z2Max + 1)
  ;({ min: z3Min, max: z3Max } = clamp(z3Min, z3Max))

  // Z4 starts right after Z3
  z4Min = Math.max(z4Min, z3Max + 1)
  ;({ min: z4Min, max: z4Max } = clamp(z4Min, z4Max))

  // Z5 starts right after Z4
  z5Min = Math.max(z5Min, z4Max + 1)
  if (z5Max !== null) {
    ;({ min: z5Min, max: z5Max } = clamp(z5Min, z5Max))
  } else {
    // If we still lack an upper bound, leave Z5 open-ended with min only
    z5Min = Math.max(0, z5Min)
  }

  return [
    { min: z1Min, max: z1Max },
    { min: z2Min, max: z2Max },
    { min: z3Min, max: z3Max },
    { min: z4Min, max: z4Max },
    { min: z5Min, max: z5Max },
  ]
}

/**
 * Calculate pace zones based on VT1 and VT2 pace
 * Note: For pace, higher values = slower pace
 */
export const calculatePaceZones = (zoneModel: ZoneModel, thresholds: ThresholdValues): CalculatedZone[] => {
  const { vt1PaceSeconds, vt2PaceSeconds } = thresholds

  if (!vt1PaceSeconds && !vt2PaceSeconds) {
    return zoneModel.zones.map((zone) => ({
      ...zone,
      minValue: null,
      maxValue: null,
    }))
  }

  return zoneModel.zones.map((zone, index) => {
    let minValue: number | null = null // Faster pace (lower time)
    let maxValue: number | null = null // Slower pace (higher time)

    // For 6-zone pace model
    if (index === 0) {
      // Z1: Easy (slower than VT1 + 20%)
      minValue = vt1PaceSeconds || null
      maxValue = vt1PaceSeconds ? Math.round(vt1PaceSeconds * 1.2) : null
    } else if (index === 1) {
      // Z2: Moderate (VT1 to VT1 + 10%)
      minValue = vt1PaceSeconds ? Math.round(vt1PaceSeconds * 0.95) : null
      maxValue = vt1PaceSeconds || null
    } else if (index === 2) {
      // Z3: Tempo (VT1 - 5% to VT2 + 5%)
      minValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.95) : null
      maxValue = vt1PaceSeconds ? Math.round(vt1PaceSeconds * 0.95) : null
    } else if (index === 3) {
      // Z4: Threshold (VT2 - 5% to VT2)
      minValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.95) : null
      maxValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 1.05) : null
    } else if (index === 4) {
      // Z5: VO2max (VT2 - 10% to VT2 - 5%)
      minValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.9) : null
      maxValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.95) : null
    } else if (index === 5) {
      // Z6: Anaerobic (faster than VT2 - 10%)
      minValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.8) : null
      maxValue = vt2PaceSeconds ? Math.round(vt2PaceSeconds * 0.9) : null
    }

    return {
      ...zone,
      minValue,
      maxValue,
    }
  })
}

/**
 * Calculate power zones based on VT2 power (FTP)
 * Uses VT2 power as the functional threshold power (FTP)
 */
export const calculatePowerZones = (zoneModel: ZoneModel, thresholds: ThresholdValues): CalculatedZone[] => {
  const { vt2PowerWatts } = thresholds

  if (!vt2PowerWatts) {
    return zoneModel.zones.map((zone) => ({
      ...zone,
      minValue: null,
      maxValue: null,
    }))
  }

  return zoneModel.zones.map((zone) => {
    const minValue = Math.round((vt2PowerWatts * zone.minPercent) / 100)
    const maxValue = Math.round((vt2PowerWatts * zone.maxPercent) / 100)

    return {
      ...zone,
      minValue,
      maxValue,
    }
  })
}

/**
 * Generic zone calculation function that delegates to specific calculators
 */
export const calculateZones = (zoneModel: ZoneModel, thresholds: ThresholdValues): CalculatedZone[] => {
  switch (zoneModel.type) {
    case 'heart_rate':
      return calculateHRZones(zoneModel, thresholds)
    case 'pace':
      return calculatePaceZones(zoneModel, thresholds)
    case 'power':
      return calculatePowerZones(zoneModel, thresholds)
    default:
      return zoneModel.zones?.map((zone) => ({
        ...zone,
        minValue: null,
        maxValue: null,
      }))
  }
}

/**
 * Format pace in seconds to mm:ss format
 */
export const formatPaceSeconds = (seconds: number | null): string => {
  if (seconds === null) return '-'
  const minutes = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Format zone value based on type
 */
export const formatZoneValue = (value: number | null, type: 'heart_rate' | 'pace' | 'power'): string => {
  if (value === null) return '-'

  switch (type) {
    case 'heart_rate':
      return `${Math.round(value)} bpm`
    case 'pace':
      return `${formatPaceSeconds(value)}/km`
    case 'power':
      return `${Math.round(value)}W`
    default:
      return `${value}`
  }
}

/**
 * Format zone range based on type
 */
export const formatZoneRange = (
  minValue: number | null,
  maxValue: number | null,
  type: 'heart_rate' | 'pace' | 'power'
): string => {
  if (minValue === null && maxValue === null) {
    return 'Not configured'
  }

  const min = formatZoneValue(minValue, type)
  const max = formatZoneValue(maxValue, type)

  return `${min} - ${max}`
}
